import { useContext, useEffect } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useTrainingModule, { useTrainingModuleForm } from '@hooks/useTrainingModule'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { TrainingModuleModel } from '@models/types'

const MODAL_KEY = 'CreateOrDuplicateTrainingModuleModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTrainingModule?: (payload: HandleCallbackActionParams) => void,
  duplicateTrainingModule?: (payload: HandleCallbackActionParams) => void,
}

type CreateOrDuplicateTrainingModulePayload = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<TrainingModuleModel>,
  duplicateFn: Function,
}

const createOrDuplicateTrainingModule = (params: CreateOrDuplicateTrainingModulePayload) => {
  const {
    callbacks, createFn, duplicateFn, entityState,
  } = params

  const { closeModal, createTrainingModule, duplicateTrainingModule } = callbacks

  const [text, actionFn] = entityState.id ? ['Duplicated', duplicateFn] : ['Created', createFn]

  const trainingModulePayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    requestOptions: {
      include_training_module_training_course: true,
      include_training_modules_count: true,
    },
    toastText: `Training Module ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return duplicateTrainingModule!(trainingModulePayload)

  // @ts-expect-error TS2345
  return createTrainingModule!(trainingModulePayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateTrainingModuleModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { trainingModule: initTrainingModule } = modalPayload

  const trainingModulePayload = useTrainingModule(initTrainingModule)

  const {
    callbacks: {
      createTrainingModule: createFn,
      duplicateTrainingModule: duplicateFn,
    },
    creating,
    trainingModule,
  } = trainingModulePayload

  const {
    entityState,
    entityState: {
      id,
      title,
      training_course_id,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  // @ts-expect-error TS2345
  } = useTrainingModuleForm(trainingModule, { validateOn: 'change' })

  const createOrDuplicateTrainingModulePayload = {
    callbacks,
    createFn,
    duplicateFn,
    entityState,
  }

  const saveButtonText = id ? 'Duplicate Training Module' : 'Create Training Module'

  useEffect(() => {
    if (id) setEntityState({ title: `${trainingModule.title} Copy` })
  }, [id])

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Duplicate' : 'Create'}
        titleSecondLine="Training Module"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          {id && (
            <EntitySelector
              callbacks={{
                // @ts-expect-error TS2322
                selectItem: (trainingCourse: { id?: number }) => setEntityState(
                  { training_course_id: trainingCourse ? trainingCourse.id : null },
                ),
              }}
              endpoint="training_courses"
              entityKey="trainingCourses"
              errorMessage={errors.training_course_id}
              label="* Training Course"
              marginTop="large"
              selectedItemId={training_course_id}
            />
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createOrDuplicateTrainingModule(createOrDuplicateTrainingModulePayload)}
          size="large"
        >
          {saveEnabled ? saveButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateTrainingModuleModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
